import { StoreStockData } from 'common/components/stocks-in-stores/types';
import log from 'loglevel';
import { useEffect, useRef } from 'react';
import { YaPaySession } from 'types/yandex-pay';

type Args = {
  merchantId: string;
  price?: number | null;
  debouncedQuantity: number;
  env: string;
  version: number;
  currency: string;
  availablePaymentMethods: string[];
  setIsWidgetMounted: (mounted: boolean) => void;
  stocks: StoreStockData[];
  hideWidgetHeader: boolean;
};

export const useYandexPayWidget = ({
  merchantId,
  price,
  debouncedQuantity,
  env,
  version,
  currency,
  availablePaymentMethods,
  setIsWidgetMounted,
  stocks,
  hideWidgetHeader,
}: Args) => {
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!stocks.length || !merchantId || !price || !widgetRef.current) return;

    let session: YaPaySession | null = null;
    let observer: MutationObserver | null = null;
    let mountedTimeout: number | null = null;

    const totalAmount = (price * debouncedQuantity).toFixed(2);

    async function init() {
      if (!window.YaPay || !widgetRef.current) return;

      const YaPay = window.YaPay;

      observer = new MutationObserver(() => {
        const iframe = widgetRef.current?.querySelector('iframe');
        if (iframe) {
          mountedTimeout = window.setTimeout(() => setIsWidgetMounted(true), 1500);
          observer?.disconnect();
        }
      });

      observer.observe(widgetRef.current, { childList: true, subtree: true });

      session = (await YaPay.createSession(
        {
          env: YaPay.PaymentEnv[env],
          version,
          currencyCode: currency,
          merchantId,
          totalAmount,
          availablePaymentMethods,
        },
        { onPayButtonClick: () => {}, onFormOpenError: (error) => log.error(error) },
      )) as YaPaySession;

      widgetRef.current.innerHTML = '';
      session.mountWidget(widgetRef.current, {
        widgetType: YaPay.WidgetType.Ultimate,
        widgetTheme: YaPay.WidgetTheme.Light,
        borderRadius: 20,
        padding: YaPay.WidgetPaddingType.Default,
        withOutline: false,
        widgetBackground: YaPay.WidgetBackgroundType.Default,
        hideWidgetHeader: hideWidgetHeader,
        widgetSize: YaPay.WidgetSize.Medium,
      });
    }

    init();

    return () => {
      session?.destroy?.();
      observer?.disconnect();
      if (mountedTimeout) clearTimeout(mountedTimeout);
    };
  }, [
    merchantId,
    price,
    debouncedQuantity,
    env,
    version,
    currency,
    availablePaymentMethods,
    setIsWidgetMounted,
    stocks.length,
    hideWidgetHeader,
  ]);

  return widgetRef;
};
